import {
  LINK_TARGET_DEFAULT,
  LINK_TARGET_NEW_TAB,
} from '@/helpers/constants.js'
import { getInstanceField } from '@/hooks/useGetInstanceField'
import { useState } from '@/hooks/vue-utils/useVuex'
import {
  ROUTE_AI_ASSISTANTS,
  ROUTE_CERTIFICATES,
  ROUTE_COMPANY_SETTINGS,
  ROUTE_COMPANY_SUBSIDIARIES,
  ROUTE_DASHBOARD,
  ROUTE_EVENTS,
  ROUTE_FAQ,
  ROUTE_GROUPS,
  ROUTE_NEWS,
  ROUTE_POLICIES,
  ROUTE_PORTAL,
  ROUTE_PORTAL_CREATOR,
  ROUTE_PORTAL_LAYOUT,
  ROUTE_PRODUCTS,
  ROUTE_UNAUTHORIZED,
  ROUTE_USER_APPROVALS,
  ROUTE_USERS,
  ROUTE_USERS_MY_USERS,
} from '@/router/routes/routeNames'
import { reactive, ref, unref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'

import { portalLogoIcon } from '@/assets/img'
import {
  aiAssistantsIconIcon,
  settingsIconIcon,
  companySubsidiariesIcon,
  dashboardIconIcon,
  eventsIconIcon,
  faqIconIcon,
  policiesIconIcon,
  portalLayoutIconIcon,
  productsIconIcon,
  usersIconIcon,
} from '@/assets/img/navigation'
import {
  type CompanyFeatureFlags,
  keepsLegacyDashboard,
  openInsightsInNewTab,
} from '@/helpers/insights'
import { getPortalLink, getPortalLinkTarget } from '@/helpers/portalLink.js'
import { checkAccessForSidebar } from '@/helpers/routeAccessValidation.js'
import { AI_ASSISTANTS_FEATURE_FLAG } from '@/hooks/aiAssistants/useAiAssistantsAccess'
import { useI18n } from '@/hooks/useI18N'
import { useToast } from '@/hooks/useToast'
import * as Sentry from '@sentry/vue'

interface NavigationItem {
  text: string
  name: string
  link: string // to be removed
  subject: string
  target: string
  icon?: any
  isOpen?: boolean
  externalURL?: string
  // Runs on click instead of routing to the item
  action?: () => void
  show?: boolean
  selected?: boolean
  children?: NavigationItem[]
}

/**
 * Assumes $ability exists on vue instance
 */
export const useGlobalNavigation = () => {
  const $ability = getInstanceField('$ability')
  const i18n = useI18n()
  const { doToast } = useToast()
  const dashboardItem = reactive<NavigationItem>({
    text: 'navigation.SIDEBAR_DASHBOARD',
    name: ROUTE_DASHBOARD,
    link: '/dashboard',
    subject: 'enable_reports_dashboard',
    icon: dashboardIconIcon,
    isOpen: false,
    target: LINK_TARGET_DEFAULT,
    // Declared up front: Vue 2 does not track keys added to a reactive object later
    action: undefined,
    children: [],
  })
  const items = ref<NavigationItem[]>([
    {
      text: 'navigation.SIDEBAR_USERS',
      name: ROUTE_USERS,
      link: '/users', // TODO: Remove links and rely on the named route.
      subject: 'User',
      icon: usersIconIcon,
      isOpen: false,
      target: LINK_TARGET_DEFAULT,
      children: [
        {
          text: 'navigation.SIDEBAR_MY_USERS',
          name: ROUTE_USERS_MY_USERS,
          link: '/users/my-users',
          subject: 'User',
          selected: false,
          target: LINK_TARGET_DEFAULT,
        },
        {
          text: 'navigation.SIDEBAR_COMPANY_GROUPS',
          name: ROUTE_GROUPS,
          link: '/users/groups',
          subject: 'Groups',
          selected: false,
          target: LINK_TARGET_DEFAULT,
        },
        {
          text: 'navigation.SIDEBAR_APPROVE',
          name: ROUTE_USER_APPROVALS,
          link: '/users/approvals',
          subject: 'Approve',
          selected: false,
          target: LINK_TARGET_DEFAULT,
        },
      ],
    },
    {
      text: 'navigation.SIDEBAR_PRODUCTS',
      name: ROUTE_PRODUCTS,
      link: '/products',
      subject: 'Product',
      icon: productsIconIcon,
      isOpen: false,
      target: LINK_TARGET_DEFAULT,
      children: [],
    },
    {
      text: 'navigation.SIDEBAR_EVENTS',
      name: ROUTE_EVENTS,
      link: '/events',
      subject: 'Events',
      icon: eventsIconIcon,
      isOpen: false,
      target: LINK_TARGET_DEFAULT,
      children: [],
    },
    {
      text: 'navigation.SIDEBAR_PORTAL_CREATOR',
      name: ROUTE_PORTAL_CREATOR,
      link: '/portal-creator',
      subject: 'ProductCategories',
      icon: portalLayoutIconIcon,
      isOpen: false,
      target: LINK_TARGET_DEFAULT,
      children: [
        {
          text: 'navigation.SIDEBAR_PORTAL_LAYOUT',
          name: ROUTE_PORTAL_LAYOUT,
          link: '/portal-creator/portal-layout',
          subject: 'ProductCategories',
          selected: false,
          target: LINK_TARGET_DEFAULT,
        },
        {
          text: 'navigation.SIDEBAR_NEWS',
          name: ROUTE_NEWS,
          link: '/portal-creator/news',
          subject: 'News',
          selected: false,
          target: LINK_TARGET_DEFAULT,
        },
        {
          text: 'navigation.SIDEBAR_CERTIFICATES',
          name: ROUTE_CERTIFICATES,
          link: '/portal-creator/certificates',
          subject: 'Certificates',
          selected: false,
          target: LINK_TARGET_DEFAULT,
        },
      ],
    },
    {
      text: 'navigation.SIDEBAR_POLICIES',
      name: ROUTE_POLICIES,
      link: '/policies',
      subject: 'Policies',
      icon: policiesIconIcon,
      isOpen: false,
      target: LINK_TARGET_DEFAULT,
      children: [],
    },
    {
      text: 'navigation.SIDEBAR_COMPANY_SUBSIDIARIES',
      name: ROUTE_COMPANY_SUBSIDIARIES,
      link: '/company-subsidiaries',
      subject: 'Company',
      icon: companySubsidiariesIcon,
      isOpen: false,
      target: LINK_TARGET_DEFAULT,
      children: [],
    },
    {
      text: 'navigation.SIDEBAR_AI_ASSISTANTS',
      name: ROUTE_AI_ASSISTANTS,
      link: '/ai-assistants',
      subject: AI_ASSISTANTS_FEATURE_FLAG,
      icon: aiAssistantsIconIcon,
      isOpen: false,
      target: LINK_TARGET_DEFAULT,
      children: [],
    },
    dashboardItem,
    {
      text: 'navigation.SIDEBAR_COMPANY_SETTINGS',
      name: ROUTE_COMPANY_SETTINGS,
      link: '/company-settings',
      subject: 'CompanySettings',
      icon: settingsIconIcon,
      isOpen: false,
      target: LINK_TARGET_DEFAULT,
      children: [],
    },
  ])

  const portalItem = ref<NavigationItem>({
    text: 'navigation.SIDEBAR_PORTAL',
    name: ROUTE_PORTAL,
    link: '', // To be removed.
    subject: '',
    icon: portalLogoIcon,
    isOpen: false,
    target: getPortalLinkTarget(),
    externalURL: getPortalLink(),
    children: [],
  })

  const faqItem = reactive<NavigationItem>({
    text: 'DO_YOU_NEED_HELP',
    name: ROUTE_FAQ,
    link: '/faq',
    subject: 'faq',
    icon: faqIconIcon,
    isOpen: false,
    target: LINK_TARGET_DEFAULT,
    children: [],
  })

  const route = useRoute()
  const router = useRouter()

  const { currentCompany, currentUser } = useState('common', [
    'currentCompany',
    'currentUser',
  ])

  // Companies not kept on the legacy dashboard open Insights straight from this
  // tab. A plain new-tab link to /dashboard would cold-load Manager there, without
  // the login session that lives in this tab's sessionStorage.
  watch(
    // The common store module is untyped JS, so `useState` cannot type the company.
    () => currentCompany.value as CompanyFeatureFlags | null,
    (company) => {
      const opensInsights = !keepsLegacyDashboard(company)
      dashboardItem.target = opensInsights
        ? LINK_TARGET_NEW_TAB
        : LINK_TARGET_DEFAULT
      dashboardItem.action = opensInsights ? openInsightsDashboard : undefined
    },
    { immediate: true }
  )

  async function openInsightsDashboard() {
    try {
      await openInsightsInNewTab()
    } catch (error: unknown) {
      Sentry.captureException(error)
      doToast({ text: i18n.t('SOMETHING_WENT_WRONG'), theme: 'error' })
    }
  }

  watch(
    () => route.fullPath,
    () => {
      setSelectedNavigationItem()
    }
  )

  function getAllSidebarItems() {
    return [...unref(items), unref(faqItem)]
  }

  function setSelectedNavigationItem() {
    const sidebarItems = getAllSidebarItems()
    sidebarItems.forEach((item) => {
      item.isOpen = false
      if (routeIncludesItemLink(item.link) && showItem(item)) {
        item.isOpen = true
      } else if (!showItem(item) && routeIncludesItemLink(item.link)) {
        redirectToFirstAvailable()
      }
    })
  }

  function redirectToFirstAvailable() {
    const availableItems = unref(items).filter((item) => showItem(item))
    if (availableItems.length === 0) {
      router.push({
        name: ROUTE_UNAUTHORIZED,
        params: { allowTakeMeHome: 'false' },
      })
      return
    }
    availableItems[0].isOpen = true
    router.push({ name: availableItems[0].name })
  }

  function routeIncludesItemLink(itemLink: string) {
    return route.path.includes(itemLink)
  }

  function showItem(item: NavigationItem) {
    return checkAccessForSidebar(
      currentCompany.value,
      currentUser.value,
      router,
      $ability,
      item
    )
  }

  setSelectedNavigationItem()
  return {
    faqItem,
    portalItem,
    items,
    showItem,
  }
}
